import React, {useEffect, useRef, useState} from 'react';
import {generateMicroreelText} from '../generateMicroreelText';
import {parseStringArrayToInt} from '../../../../utils/parseStringArrayToInt';
import TextAreaInput from '../../../../uiToolkit/Inputs/TextAreaInput';
import TextInput from '../../../../uiToolkit/Inputs/TextInput';
import Dropdown from '../../../../uiToolkit/Inputs/Dropdown';
import Button from '../../../../uiToolkit/Buttons/Button';
import Tooltip from '../../../../uiToolkit/Tooltip';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import PdfPreview from '../components/PdfPreview';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import {
  MicroreelGenerationModel,
  MicroreelSlideGenerationModel,
  MicroreelVideoGenerationModel,
} from '../../../../data/models/Microreels/MicroreelGenerationModel';
import {SelectOption} from '../../../../data/models/UI/SelectOption';
import {Subject} from '../../../../data/models/Course/LearningStandards';
import {
  BACKGROUND_MUSIC_OPTIONS,
  DialectKey,
  Gender,
  LANGUAGE_CONFIG,
  getDialectLabel,
  LANGUAGE_OPTIONS,
  LanguageKey,
} from '../../../../data/models/AI/VideoGenerationOptions';
import {MicroreelGenerationInfo} from '../../../Microreels/Generating/generatingTypes';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {logoTextWhite} from '../../../../assets/images';
import styles from './GenerateMicroreelUI.module.css';

// Direct upload on the Source card is hidden: files come in through the
// library modal ("Select file"), which has its own upload. Flip to restore
// the native-picker button (the whole flow behind it still works).
const SHOW_UPLOAD_BUTTON = false;

interface Props {
  onCancel: () => void;
  isLoading: boolean;
  onGenerateFromPrompt: (
    model: MicroreelGenerationModel,
    info: MicroreelGenerationInfo,
  ) => void;
  onGenerateFromSlide: (
    model: MicroreelSlideGenerationModel,
    info: MicroreelGenerationInfo,
  ) => void;
  onGenerateFromVideo: (
    model: MicroreelVideoGenerationModel,
    info: MicroreelGenerationInfo,
  ) => void;
  onBrowseDocument: () => Promise<MediaFile | undefined>;
  onUploadDocument: (file: File) => Promise<MediaFile | undefined>;
  onBrowseAudio: () => Promise<MediaFile | undefined>;
  onSuggestTitle: (fileName: string) => Promise<string>;
  onViewMicroreels: () => void;
  isGenerationAvailable: boolean;
  generationStarted: boolean;
  subjects?: Subject[];
  subjectsLoading?: boolean;
  subjectsError?: boolean;
  onReloadSubjects?: () => void;
}

const ACTION_NONE = 'none';
const ACTION_ASSESSMENT = 'assessment';
const ACTION_ACK = 'ack';

const NO_MUSIC_KEY = 'none';
const LIBRARY_MUSIC_KEY = 'library';

// generate_from_slide crashes server-side when NumberOfBranches is null
// (unguarded .Value), so the slide flow must always send one. 5 matches the
// backend's own AiHelper.DefaultBranchingNumber.
const SLIDE_DEFAULT_REELS = 5;
// generate_from_prompt defaults NumberOfBranches to 4 server-side; sent
// explicitly to keep the passing score math visible.
const PROMPT_DEFAULT_REELS = 4;
// Passing score for generated knowledge checks. The slide and video
// endpoints read the percentage directly (passingScore); the prompt endpoint
// derives it as passingScoreNumber * 100 / numberOfBranches.
const PASSING_SCORE_PERCENT = 50;
const PROMPT_PASSING_CORRECT = Math.round(
  (PROMPT_DEFAULT_REELS * PASSING_SCORE_PERCENT) / 100,
);

const CHUNK_DURATION_BY_LENGTH: Record<string, number> = {
  [generateMicroreelText.lengthShort]: 0,
  [generateMicroreelText.lengthMedium]: 1,
  [generateMicroreelText.lengthLong]: 2,
};

const isPdf = (file: MediaFile) => file.fileType === 'application/pdf';

const isVideo = (file: MediaFile) => file.fileType.startsWith('video');

const OFFICE_FILE_TYPES = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

// Office docs can't be rendered by the browser; the Microsoft Office Online
// viewer renders them from the signed storage URL. Note: this shares the
// (expiring) signed URL with Microsoft's service.
const isOfficeDoc = (file: MediaFile) =>
  OFFICE_FILE_TYPES.includes(file.fileType);

const officeViewerUrl = (fileUrl: string) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    fileUrl,
  )}`;

const EXCLUDE_PAGES_MAX_LENGTH = 150;

// Client-side estimates shown in the top bar chips.
const ESTIMATE_BASE_MINUTES = 5;
const ESTIMATE_DOCUMENT_MINUTES = 3;
const FIXED_ESTIMATE_CREDITS = 15;

const SparklesIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
    <path d="M20 2v4" />
    <path d="M22 4h-4" />
    <circle cx="4" cy="20" r="2" />
  </svg>
);

const SettingsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden>
    <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M8 4.8V8l2.2 1.6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const CreditsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M8 1.6l1.5 1.7 2.2-.4.4 2.2 2 1-1 2 1 2-2 .9-.4 2.2-2.2-.4L8 14.4l-1.5-1.7-2.2.4-.4-2.2-2-1 1-1.9-1-2 2-1 .4-2.2 2.2.4L8 1.6z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const GenerateMicroreelUI = React.memo(
  ({
    onCancel,
    isLoading,
    onGenerateFromPrompt,
    onGenerateFromSlide,
    onGenerateFromVideo,
    onBrowseDocument,
    onUploadDocument,
    onBrowseAudio,
    onSuggestTitle,
    onViewMicroreels,
    isGenerationAvailable,
    generationStarted,
    subjects = [],
    subjectsLoading = false,
    subjectsError = false,
    onReloadSubjects,
  }: Props) => {
    const [isPublic, setIsPublic] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState<Subject | null>(
      null,
    );
    const subjectId = selectedSubject?.id ?? null;
    const availableSubjects =
      selectedSubject && !subjects.some(subject => subject.id === subjectId)
        ? [...subjects, selectedSubject]
        : subjects;
    const [sourceFile, setSourceFile] = useState<MediaFile>();
    const [previewPage, setPreviewPage] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [pdfFailed, setPdfFailed] = useState(false);
    const [previewFailed, setPreviewFailed] = useState(false);
    const [excludePages, setExcludePages] = useState('');

    // Exclude pages accepts page numbers and ranges: digits, commas, dashes.
    const onExcludePagesChange = (value: string) => {
      setExcludePages(
        value.replace(/[^0-9,-]/g, '').slice(0, EXCLUDE_PAGES_MAX_LENGTH),
      );
    };
    const [summarize, setSummarize] = useState(false);
    const [title, setTitle] = useState('');
    const [prompt, setPrompt] = useState('');
    const [lengthOption, setLengthOption] = useState(
      generateMicroreelText.lengthShort,
    );
    const [musicType, setMusicType] = useState<string>(NO_MUSIC_KEY);
    const [musicFile, setMusicFile] = useState<MediaFile>();
    const [endAction, setEndAction] = useState(ACTION_ASSESSMENT);
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const [visualsOption, setVisualsOption] = useState(
      generateMicroreelText.visualsGenerated,
    );
    const [mediaSource, setMediaSource] = useState<
      'storyblocks' | 'ai' | 'both'
    >('storyblocks');
    // Narration depth is hidden from the form for now (nothing consumes it
    // server-side); the label still feeds the generating page's details card.
    const [narrationOption] = useState(generateMicroreelText.narrationStandard);
    const [language, setLanguage] = useState<LanguageKey>('en');
    const [dialectOptions, setDialectOptions] = useState<SelectOption[]>([]);
    const [selectedDialect, setSelectedDialect] = useState<DialectKey>('US');
    const [voiceGender, setVoiceGender] = useState<Gender>('male');

    const locked = isLoading || generationStarted;

    useEffect(() => {
      const config = LANGUAGE_CONFIG[language];
      if (!config) return;
      const dialects = Object.entries(config.dialects);
      const options: SelectOption[] = dialects.map(([dialectKey]) => ({
        key: dialectKey,
        value: getDialectLabel(language, dialectKey),
      }));
      setDialectOptions(options);
      if (dialects.length > 0) {
        // English defaults to US; other languages use their first dialect.
        const defaultDialect =
          language === 'en' && config.dialects.US ? 'US' : options[0].key;
        setSelectedDialect(defaultDialect);
        setVoiceGender(config.dialects[defaultDialect]);
      } else {
        setSelectedDialect('');
        setVoiceGender('female');
      }
    }, [language]);

    const onDialectChange = (dialect: string) => {
      setSelectedDialect(dialect);
      const config = LANGUAGE_CONFIG[language];
      if (config?.dialects[dialect]) {
        setVoiceGender(config.dialects[dialect]);
      }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [promptOpen, setPromptOpen] = useState(false);

    // Tracks the last auto-filled title so a fresh source can replace it,
    // while a title the user typed themselves is never overwritten.
    const autoTitleRef = useRef('');

    const maybeSuggestTitle = async (file: MediaFile) => {
      if (title.trim() && title !== autoTitleRef.current) return;
      const suggested = await onSuggestTitle(file.fileName);
      if (!suggested) return;
      setTitle(prev => {
        if (prev.trim() && prev !== autoTitleRef.current) return prev;
        autoTitleRef.current = suggested;
        return suggested;
      });
    };

    const applySource = (file: MediaFile) => {
      setSourceFile(file);
      setPreviewPage(1);
      setNumPages(0);
      setPdfFailed(false);
      setPreviewFailed(false);
      setExcludePages('');
      setSummarize(false);
      maybeSuggestTitle(file);
    };

    const onSelectSource = async () => {
      const file = await onBrowseDocument();
      if (file) {
        applySource(file);
      }
    };

    const onUploadClick = () => {
      fileInputRef.current?.click();
    };

    const onFilePicked = async (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0];
      ev.target.value = '';
      if (!file) return;
      setUploading(true);
      const uploaded = await onUploadDocument(file);
      setUploading(false);
      if (uploaded) {
        applySource(uploaded);
      }
    };

    const onRemoveSource = () => {
      setSourceFile(undefined);
      setPreviewPage(1);
      setNumPages(0);
      setPdfFailed(false);
      setPreviewFailed(false);
      setExcludePages('');
    };

    const saveEnabled = () => {
      if (!title || selectedDialect === '') return false;
      // Without a source document the prompt is the whole input.
      if (!sourceFile && !prompt.trim()) return false;
      return true;
    };

    const sourceOnly = visualsOption === generateMicroreelText.visualsSource;
    // A video source generates through generate_from_video: the original
    // audio and scenes are kept, so voice/music/visuals do not apply.
    const videoSource = !!sourceFile && isVideo(sourceFile);

    const handleCreate = () => {
      // slideToArtifactRatio = % of each scene taken from the document itself
      // (100 = only the source content, 0 = only generated media).
      const mediaFlags = sourceOnly
        ? {generateAiImages: false, generateAiVideos: false}
        : mediaSource === 'ai'
        ? {generateAiImages: true, generateAiVideos: false}
        : mediaSource === 'both'
        ? {generateAiImages: true, generateAiVideos: true}
        : {generateAiImages: false, generateAiVideos: true};
      const visualsParams = {
        ...mediaFlags,
        slideToArtifactRatio: sourceOnly
          ? 100
          : visualsOption === generateMicroreelText.visualsPartial
          ? 50
          : 0,
      };
      const common = {
        is_public: isPublic,
        subjectId,
        ...visualsParams,
        chunkDuration: CHUNK_DURATION_BY_LENGTH[lengthOption],
        addAssessment: endAction === ACTION_ASSESSMENT,
        addAckNode: endAction === ACTION_ACK,
        language,
        dialect: selectedDialect,
        gender: voiceGender,
        name: title,
        backgroundAudio:
          musicType === NO_MUSIC_KEY || musicType === LIBRARY_MUSIC_KEY
            ? undefined
            : musicType,
        backgroundAudioFileId:
          musicType === LIBRARY_MUSIC_KEY ? musicFile?.id : undefined,
      };
      const languageName =
        LANGUAGE_OPTIONS.find(option => option.key === language)?.value ??
        language;
      const info: MicroreelGenerationInfo = {
        title,
        description: prompt,
        lengthLabel: lengthOption,
        visualsLabel: videoSource
          ? generateMicroreelText.visualsFromVideo
          : visualsOption,
        visualsHint: videoSource
          ? generateMicroreelText.visualsFromVideoHint
          : sourceOnly
          ? generateMicroreelText.visualsSourceHint
          : mediaSource === 'ai'
          ? generateMicroreelText.mediaAiImages
          : mediaSource === 'both'
          ? generateMicroreelText.mediaBoth
          : generateMicroreelText.mediaStoryblocks,
        languageLabel: videoSource
          ? languageName
          : `${languageName} ${selectedDialect}`,
        // Video sources keep the original audio, so the language only
        // applies to the on-screen text.
        languageHint: videoSource
          ? generateMicroreelText.languageOnScreenHint
          : undefined,
        languageKey: language,
        endActionKey: endAction,
        voiceName: videoSource
          ? generateMicroreelText.voiceOriginal
          : voiceGender === 'female'
          ? generateMicroreelText.voiceFemaleName
          : generateMicroreelText.voiceMaleName,
        voiceHint: videoSource
          ? generateMicroreelText.voiceOriginalHint
          : voiceGender === 'female'
          ? generateMicroreelText.voiceFemaleDetailsHint
          : generateMicroreelText.voiceMaleDetailsHint,
        narrationLabel: narrationOption,
        credits: estimatedCredits,
        sourceFileName: sourceFile?.fileName,
        sourceFileSizeMb: sourceFile
          ? Math.max(1, Math.round(sourceFile.fileSize))
          : undefined,
        sourcePages: numPages || undefined,
        sourceThumbnail: sourceFile?.placeholderUrl,
        excludedPages: excludePages || undefined,
      };
      if (sourceFile && videoSource) {
        onGenerateFromVideo(
          {
            libraryMediaFileId: sourceFile.id,
            chunkDuration: CHUNK_DURATION_BY_LENGTH[lengthOption],
            numberOfBranches: SLIDE_DEFAULT_REELS,
            passingScore: PASSING_SCORE_PERCENT,
            addAssessment: endAction === ACTION_ASSESSMENT,
            addAckNode: endAction === ACTION_ACK,
            language,
            name: title,
            is_public: isPublic,
            subjectId,
          },
          info,
        );
      } else if (sourceFile) {
        const ignoreSlides = parseStringArrayToInt(excludePages);
        onGenerateFromSlide(
          {
            ...common,
            libraryMediaFileId: sourceFile.id,
            numberOfBranches: SLIDE_DEFAULT_REELS,
            passingScore: PASSING_SCORE_PERCENT,
            addSummariseSlideStep: summarize,
            ignoreSlides: ignoreSlides.length ? ignoreSlides : undefined,
          },
          info,
        );
      } else {
        onGenerateFromPrompt(
          {
            ...common,
            prompt,
            numberOfBranches: PROMPT_DEFAULT_REELS,
            passingScoreNumber: PROMPT_PASSING_CORRECT,
          },
          info,
        );
      }
    };

    const musicOptions: SelectOption[] = [
      {key: NO_MUSIC_KEY, value: generateMicroreelText.noMusic},
      ...BACKGROUND_MUSIC_OPTIONS,
      {
        key: LIBRARY_MUSIC_KEY,
        value: musicFile
          ? musicFile.fileName
          : generateMicroreelText.musicFromLibrary,
      },
    ];

    const onMusicSelected = async (option: string) => {
      if (option !== LIBRARY_MUSIC_KEY) {
        setMusicFile(undefined);
        setMusicType(option);
        return;
      }
      const file = await onBrowseAudio();
      if (file) {
        setMusicFile(file);
        setMusicType(LIBRARY_MUSIC_KEY);
      }
    };

    const estimatedMinutes =
      ESTIMATE_BASE_MINUTES + (sourceFile ? ESTIMATE_DOCUMENT_MINUTES : 0);
    const estimatedCredits = FIXED_ESTIMATE_CREDITS;

    const renderOptionCard = (
      label: string,
      hint: string | undefined,
      selected: boolean,
      onSelect: () => void,
      disabled = false,
    ) => (
      <button
        type="button"
        key={label}
        className={`${styles.optionCard} ${
          selected ? styles.optionCardSelected : ''
        } ${disabled ? styles.optionCardDisabled : ''}`}
        onClick={onSelect}
        disabled={disabled || locked}>
        <span className={styles.optionCardLabel}>{label}</span>
        {hint && <span className={styles.optionCardHint}>{hint}</span>}
      </button>
    );

    return (
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button className={styles.cancelButton} onClick={onCancel}>
            ✕ {generateMicroreelText.cancel}
          </button>
          <div className={styles.topBarActions}>
            <span className={styles.estimateChip}>
              <ClockIcon />~{estimatedMinutes}{' '}
              {generateMicroreelText.estimatedMinutes}
            </span>
            <span className={styles.estimateChip}>
              <CreditsIcon />
              {generateMicroreelText.aroundCredits(estimatedCredits)} Credits
            </span>
            {generationStarted ? (
              <Button
                title={generateMicroreelText.viewMicroreels}
                onClick={onViewMicroreels}
                uiType="action"
              />
            ) : isGenerationAvailable ? (
              <button
                className={styles.createButton}
                onClick={handleCreate}
                disabled={!saveEnabled() || isLoading}>
                {isLoading ? (
                  <span className={styles.createButtonLoading}>
                    <LoadingSpinner size={18} />
                    {generateMicroreelText.creatingReel}
                  </span>
                ) : (
                  generateMicroreelText.createReel
                )}
              </button>
            ) : (
              <Tooltip
                tooltipText={
                  generateMicroreelText.generationUnavailableTooltip
                }>
                <button className={styles.createButton} disabled>
                  {generateMicroreelText.createReel}
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        <h1 className={styles.pageTitle}>
          {generateMicroreelText.newMicroreel}
        </h1>

        <div className={styles.body}>
          <div className={styles.sourceColumn}>
            <div className={styles.sourceCard}>
              <div className={styles.sourceHeader}>
                <h2 className={styles.sourceTitle}>
                  {generateMicroreelText.source}
                </h2>
              </div>
              <span className={styles.sourceHint}>
                <span className={styles.sourceHintDiamond}>◇</span>
                {generateMicroreelText.sourceHint}
              </span>
              {sourceFile ? (
                <>
                  <div className={styles.sourceFileCard}>
                    <p className={styles.sourceFileName}>
                      {sourceFile.fileName}
                    </p>
                    <span className={styles.sourceFileMeta}>
                      {Math.max(1, Math.round(sourceFile.fileSize))} MB
                    </span>
                    {isPdf(sourceFile) && sourceFile.fileUrl && !pdfFailed ? (
                      <>
                        <div className={styles.sourcePdfContainer}>
                          <PdfPreview
                            url={sourceFile.fileUrl}
                            page={previewPage}
                            className={styles.sourcePdfCanvas}
                            onDocumentLoaded={setNumPages}
                            onError={() => setPdfFailed(true)}
                          />
                        </div>
                        <div className={styles.sourcePager}>
                          <button
                            className={styles.sourcePagerButton}
                            onClick={() =>
                              setPreviewPage(page => Math.max(1, page - 1))
                            }
                            disabled={previewPage <= 1}>
                            &lt;
                          </button>
                          <span className={styles.sourcePagerLabel}>
                            {generateMicroreelText.page} {previewPage}
                            {numPages
                              ? ` ${generateMicroreelText.pageOf} ${numPages}`
                              : ''}
                          </span>
                          <button
                            className={styles.sourcePagerButton}
                            onClick={() => setPreviewPage(page => page + 1)}
                            disabled={
                              numPages !== 0 && previewPage >= numPages
                            }>
                            &gt;
                          </button>
                        </div>
                      </>
                    ) : isOfficeDoc(sourceFile) && sourceFile.fileUrl ? (
                      <iframe
                        className={styles.sourceOfficeFrame}
                        title={sourceFile.fileName}
                        src={officeViewerUrl(sourceFile.fileUrl)}
                      />
                    ) : sourceFile.placeholderUrl && !previewFailed ? (
                      <img
                        className={styles.sourcePreview}
                        src={sourceFile.placeholderUrl}
                        alt={sourceFile.fileName}
                        onError={() => setPreviewFailed(true)}
                      />
                    ) : videoSource ? (
                      <div className={styles.sourceLogoPlaceholder}>
                        <img src={logoTextWhite} alt="" />
                      </div>
                    ) : (
                      <div className={styles.sourcePlaceholder}>
                        <span className="icon-file" />
                      </div>
                    )}
                    {!videoSource && (
                      <>
                        <p className={styles.excludeTitle}>
                          {generateMicroreelText.excludePages}
                        </p>
                        <TextInput
                          value={excludePages}
                          placeholder={
                            generateMicroreelText.excludePagesPlaceholder
                          }
                          onChange={onExcludePagesChange}
                          maxCharacters={EXCLUDE_PAGES_MAX_LENGTH}
                        />
                        <div className={styles.summarizeRow}>
                          <div className={styles.summarizeHeader}>
                            <span className={styles.summarizeTitle}>
                              {generateMicroreelText.summarizeTitle}
                            </span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={summarize}
                              className={`${styles.switch} ${
                                summarize ? styles.switchOn : ''
                              }`}
                              onClick={() => {
                                const next = !summarize;
                                // Summarized scenes have no source pages to
                                // show, so only fully generated visuals apply.
                                if (next) {
                                  setVisualsOption(
                                    generateMicroreelText.visualsGenerated,
                                  );
                                }
                                setSummarize(next);
                              }}>
                              <span className={styles.switchKnob} />
                            </button>
                          </div>
                          <span className={styles.summarizeHint}>
                            {generateMicroreelText.summarizeHint}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className={styles.sourceActions}>
                    <button
                      className={styles.sourceReplace}
                      onClick={onSelectSource}
                      disabled={locked}>
                      {generateMicroreelText.replace}
                    </button>
                    <button
                      className={styles.sourceRemove}
                      onClick={onRemoveSource}
                      disabled={locked}>
                      {generateMicroreelText.remove}
                    </button>
                  </div>
                </>
              ) : (
                <div className={styles.sourceEmptyState}>
                  <Icon
                    name="microreel-source.svg"
                    className={styles.sourceIllustration}
                  />
                  <p className={styles.sourceEmptyTitle}>
                    {generateMicroreelText.sourceEmptyTitle}
                  </p>
                  <p className={styles.sourceEmptyDrop}>
                    {generateMicroreelText.sourceEmptyDrop}
                  </p>
                  <div className={styles.sourceEmptyButtons}>
                    {SHOW_UPLOAD_BUTTON && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.docx,.pptx,.mp4,.m4v,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,video/mp4,video/x-m4v"
                          className={styles.hiddenFileInput}
                          onChange={onFilePicked}
                        />
                        <button
                          className={styles.sourcePillButton}
                          onClick={onUploadClick}
                          disabled={locked || uploading}>
                          {uploading
                            ? generateMicroreelText.uploading
                            : generateMicroreelText.uploadFile}
                        </button>
                      </>
                    )}
                    <button
                      className={styles.sourcePillButton}
                      onClick={onSelectSource}
                      disabled={locked || uploading}>
                      {generateMicroreelText.selectFile}
                    </button>
                  </div>
                  <p className={styles.sourceFormats}>
                    {generateMicroreelText.sourceFormats}
                  </p>
                  <p className={styles.sourceNoFile}>
                    <strong>{generateMicroreelText.sourceNoFileBold}</strong>{' '}
                    {generateMicroreelText.sourceNoFileText}
                  </p>
                  {promptOpen || prompt ? (
                    <div className={styles.sourcePromptBlock}>
                      <p className={styles.fieldTitle}>
                        {generateMicroreelText.teachTitle}
                        <span className="MandatoryIndicator">*</span>
                      </p>
                      <TextAreaInput
                        placeholder={generateMicroreelText.promptPlaceholder}
                        value={prompt}
                        onChange={setPrompt}
                        maxCharacters={250}
                      />
                    </div>
                  ) : (
                    <button
                      className={`${styles.sourcePillButton} ${styles.sourcePromptButton}`}
                      onClick={() => setPromptOpen(true)}
                      disabled={locked}>
                      <SparklesIcon />
                      {generateMicroreelText.writePrompt}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <fieldset className={styles.formColumn} disabled={locked}>
            <p className={styles.fieldTitle}>
              {generateMicroreelText.titleField}
              <span className="MandatoryIndicator">*</span>
            </p>
            <TextInput
              value={title}
              placeholder={generateMicroreelText.titleField}
              onChange={setTitle}
              maxCharacters={40}
            />

            <p className={styles.fieldTitle}>Course Category</p>
            <Dropdown
              ariaLabel="Course Category"
              options={[
                {key: '', value: 'No category'},
                ...availableSubjects.map(subject => ({
                  key: subject.id,
                  value: subject.title,
                })),
              ]}
              selectedOption={
                subjectsLoading && !subjectId ? undefined : subjectId ?? ''
              }
              setSelectedOption={value =>
                setSelectedSubject(
                  availableSubjects.find(subject => subject.id === value) ??
                    null,
                )
              }
              disabled={locked || subjectsLoading || subjectsError}
              placeholder={
                subjectsLoading ? 'Loading categories…' : 'No category'
              }
            />
            {subjectsError && (
              <p role="alert" className={styles.summarizeHint}>
                Could not load course categories.{' '}
                <button
                  type="button"
                  onClick={onReloadSubjects}
                  disabled={locked}>
                  Retry
                </button>
              </p>
            )}
            <div className={styles.courseVisibility}>
              <label>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={event => setIsPublic(event.target.checked)}
                />{' '}
                Public Course
              </label>
              <p className={styles.summarizeHint}>
                Available in the public course catalogue after publishing.
              </p>
            </div>

            <p className={styles.fieldTitle}>
              {generateMicroreelText.lengthTitle}
            </p>
            <div className={styles.optionRow}>
              {[
                generateMicroreelText.lengthShort,
                generateMicroreelText.lengthMedium,
                generateMicroreelText.lengthLong,
              ].map(option =>
                renderOptionCard(
                  option,
                  undefined,
                  lengthOption === option,
                  () => setLengthOption(option),
                ),
              )}
            </div>

            {!videoSource && (
              <>
                <p className={styles.fieldTitle}>
                  {generateMicroreelText.backgroundMusic}
                </p>
                <Dropdown
                  options={musicOptions}
                  selectedOption={musicType}
                  setSelectedOption={onMusicSelected}
                  className={styles.musicDropdown}
                />
              </>
            )}

            <p className={styles.fieldTitle}>
              {generateMicroreelText.actionsTitle}
            </p>
            <div className={styles.optionRow}>
              {renderOptionCard(
                generateMicroreelText.actionKnowledgeCheck,
                undefined,
                endAction === ACTION_ASSESSMENT,
                () => setEndAction(ACTION_ASSESSMENT),
              )}
              {renderOptionCard(
                generateMicroreelText.actionAttestation,
                undefined,
                endAction === ACTION_ACK,
                () => setEndAction(ACTION_ACK),
              )}
              {renderOptionCard(
                generateMicroreelText.actionNone,
                undefined,
                endAction === ACTION_NONE,
                () => setEndAction(ACTION_NONE),
              )}
            </div>

            <div className={styles.divider} />

            <button
              type="button"
              className={styles.customizeToggle}
              onClick={() => setCustomizeOpen(open => !open)}>
              <span className={styles.customizeLabel}>
                <SettingsIcon />
                {videoSource
                  ? generateMicroreelText.customizeTitle
                  : generateMicroreelText.customizeTitleFull}
              </span>
              <span
                className={`${styles.chevron} ${
                  customizeOpen ? styles.chevronOpen : ''
                }`}>
                ▾
              </span>
            </button>

            {customizeOpen && (
              <div className={styles.customizeSection}>
                {!videoSource && (
                  <>
                    <p className={styles.fieldTitle}>
                      {generateMicroreelText.visualsTitle}
                    </p>
                    <div className={styles.optionRow}>
                      {renderOptionCard(
                        generateMicroreelText.visualsSource,
                        generateMicroreelText.visualsSourceHint,
                        visualsOption === generateMicroreelText.visualsSource,
                        () =>
                          setVisualsOption(generateMicroreelText.visualsSource),
                        summarize,
                      )}
                      {renderOptionCard(
                        generateMicroreelText.visualsPartial,
                        generateMicroreelText.visualsPartialHint,
                        visualsOption === generateMicroreelText.visualsPartial,
                        () =>
                          setVisualsOption(
                            generateMicroreelText.visualsPartial,
                          ),
                        summarize,
                      )}
                      {renderOptionCard(
                        generateMicroreelText.visualsGenerated,
                        generateMicroreelText.visualsGeneratedHint,
                        visualsOption ===
                          generateMicroreelText.visualsGenerated,
                        () =>
                          setVisualsOption(
                            generateMicroreelText.visualsGenerated,
                          ),
                      )}
                    </div>

                    {!sourceOnly && (
                      <>
                        <p className={styles.fieldTitle}>
                          {generateMicroreelText.mediaSourceTitle}
                        </p>
                        <div className={styles.optionRow}>
                          {renderOptionCard(
                            generateMicroreelText.mediaStoryblocks,
                            generateMicroreelText.mediaStoryblocksHint,
                            mediaSource === 'storyblocks',
                            () => setMediaSource('storyblocks'),
                          )}
                          {renderOptionCard(
                            generateMicroreelText.mediaAiImages,
                            generateMicroreelText.mediaAiImagesHint,
                            mediaSource === 'ai',
                            () => setMediaSource('ai'),
                          )}
                          {renderOptionCard(
                            generateMicroreelText.mediaBoth,
                            generateMicroreelText.mediaBothHint,
                            mediaSource === 'both',
                            () => setMediaSource('both'),
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                <p className={styles.fieldTitle}>
                  {generateMicroreelText.languageTitle}
                </p>
                <div className={styles.dropdownGroup}>
                  <Dropdown
                    options={LANGUAGE_OPTIONS}
                    selectedOption={language}
                    setSelectedOption={lang => setLanguage(lang as LanguageKey)}
                    className={styles.languageDropdown}
                  />
                  {!videoSource && (
                    <Dropdown
                      options={dialectOptions}
                      selectedOption={selectedDialect}
                      setSelectedOption={onDialectChange}
                      className={styles.languageDropdown}
                    />
                  )}
                </div>

                {!videoSource && (
                  <>
                    <p className={styles.fieldTitle}>
                      {generateMicroreelText.voiceTitle}
                    </p>
                    <div className={styles.optionRow}>
                      {renderOptionCard(
                        generateMicroreelText.voiceFemaleName,
                        generateMicroreelText.voiceFemaleHint,
                        voiceGender === 'female',
                        () => setVoiceGender('female'),
                      )}
                      {renderOptionCard(
                        generateMicroreelText.voiceMaleName,
                        generateMicroreelText.voiceMaleHint,
                        voiceGender === 'male',
                        () => setVoiceGender('male'),
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </fieldset>
        </div>
      </div>
    );
  },
);

export default GenerateMicroreelUI;
